import React, { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import jobsData from "../../JobOpenings.json";
import fleetImage from "../../images/brw-dump.jpg";

function Join() {
  const [capVal, setCapVal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const openings = jobsData.JobOpenings || [];

  const careerHighlights = [
    { value: "1994", label: "Founded in Central Florida" },
    { value: String(openings.length), label: "Current openings" },
    { value: "Field + Office", label: "Career paths" },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Apply",
      text: "Send your contact details, role interest, and work history.",
    },
    {
      step: "02",
      title: "Review",
      text: "Our team matches your experience to current project needs.",
    },
    {
      step: "03",
      title: "Connect",
      text: "Qualified applicants hear from BRW with the next step.",
    },
  ];

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    position: "",
    employmentHistory: "",
  });

  const formRef = useRef();

  const RECAPTCHA_SITE_KEY = "6LeqtMUpAAAAAME8SjXlQpSurZMUjSPGqZqNAOH5";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!capVal) {
      alert("Please complete the reCAPTCHA verification.");
      return;
    }

    setLoading(true);

    const serviceID = "service_8ailwss";
    const templateID = "template_4363q5s";
    const publicKey = "57rgEK2u3rLIDKJuO";

    const templateParams = {
      to_email: "careers@brwcontracting.org",
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      city: formData.city,
      position: formData.position,
      employmentHistory: formData.employmentHistory,
      "g-recaptcha-response": capVal,
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey).then(
      () => {
        setLoading(false);

        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          city: "",
          position: "",
          employmentHistory: "",
        });

        setCapVal(null);
        setShowSuccessModal(true);
      },
      () => {
        alert("Failed to send application. Please try again.");
        setLoading(false);
      }
    );
  };

  return (
    <>
      <div className="ttm-page-title ttm-bg clearfix">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-12">
              <div className="ttm-page-title-row-inner">
                <div className="page-title-heading">
                  <h2 className="title">Join Us</h2>
                </div>
                <div className="breadcrumb-wrapper">
                  <span>
                    <a title="Homepage" href="/">
                      <i className="fa fa-house" /> &nbsp; Home
                    </a>
                  </span>
                  <span>Join Us</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="brw-join">
        <div className="container">
          <div className="brw-join__hero">
            <div className="brw-join__hero-copy">
              <span className="brw-join__eyebrow">Careers at BRW Contracting</span>
              <h1>Build Florida With BRW</h1>
              <p>
                Put your experience to work on utilities, earthwork, asphalt,
                concrete, roadway, and site development projects backed by a
                company built for serious field execution.
              </p>

              <div className="brw-join__hero-actions">
                <a href="#brw-join-application" className="brw-join__primary-link">
                  Start Application
                </a>
                <a
                  href="mailto:careers@brwcontracting.org"
                  className="brw-join__secondary-link"
                >
                  Send Resume
                </a>
              </div>

              <div className="brw-join__stats" aria-label="Career highlights">
                {careerHighlights.map((item) => (
                  <div className="brw-join__stat" key={item.value}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="brw-join__media-panel">
              <img src={fleetImage} alt="BRW Contracting fleet lined up on site" />
              <div className="brw-join__media-caption">
                <span>Central Florida field teams</span>
                <strong>Built for serious field work.</strong>
              </div>
            </div>
          </div>

          <div className="brw-join__roles-panel">
            <div className="brw-join__roles-copy">
              <span>Open Roles</span>
              <h2>Choose your lane. We will match your experience to the work.</h2>
            </div>
            <div className="brw-join__role-list" aria-label="Current job openings">
              {openings.map((job) => (
                <span className="brw-join__role-pill" key={job.id}>
                  <em>{String(job.id).padStart(2, "0")}</em>
                  {job.title}
                </span>
              ))}
            </div>
          </div>

          <div className="brw-join__application-grid" id="brw-join-application">
            <aside className="brw-join__application-aside">
              <span className="brw-join__eyebrow">Application Flow</span>
              <h2>A direct path from application to conversation.</h2>
              <p>
                Share the role you want, the work you have done, and how to
                reach you. Resumes can also be sent directly to our careers
                inbox.
              </p>

              <div className="brw-join__process-list">
                {processSteps.map((item) => (
                  <div className="brw-join__process-item" key={item.step}>
                    <span>{item.step}</span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="brw-join__contact-panel">
                <i className="fa fa-envelope" aria-hidden="true"></i>
                <div>
                  <span>Resume inbox</span>
                  <a href="mailto:careers@brwcontracting.org">
                    careers@brwcontracting.org
                  </a>
                </div>
              </div>
            </aside>

            <div className="brw-join__form-card" ref={formRef}>
              <div className="brw-join__form-head">
                <span>Apply Here</span>
                <h2>Tell Us Where You Fit</h2>
              </div>

              <form onSubmit={handleSubmit} className="brw-join__form-grid">
                <p className="brw-join__form-note">
                  Complete the form and our team will review your information
                  against current openings.
                </p>

                <label className="brw-join__field">
                  <span>First Name *</span>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </label>

                <label className="brw-join__field">
                  <span>Last Name *</span>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </label>

                <label className="brw-join__field">
                  <span>Email *</span>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </label>

                <label className="brw-join__field">
                  <span>Phone *</span>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </label>

                <label className="brw-join__field">
                  <span>City *</span>
                  <input
                    type="text"
                    name="city"
                    autoComplete="address-level2"
                    required
                    value={formData.city}
                    onChange={handleChange}
                  />
                </label>

                <label className="brw-join__field brw-join__field--full">
                  <span>Position you are interested in applying for *</span>
                  <input
                    type="text"
                    name="position"
                    autoComplete="off"
                    required
                    value={formData.position}
                    onChange={handleChange}
                    placeholder="Type the role you are applying for"
                  />
                </label>

                <label className="brw-join__field brw-join__field--full">
                  <span>Tell us about your employment history *</span>
                  <textarea
                    name="employmentHistory"
                    required
                    value={formData.employmentHistory}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Share your previous employers, equipment experience, certifications, field leadership, and skills."
                  ></textarea>
                </label>

                <div className="brw-join__field--full brw-join__form-footer">
                  <div className="brw-join__captcha">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(val) => setCapVal(val)}
                      onExpired={() => setCapVal(null)}
                    />

                    {!capVal && (
                      <small>Please verify you're not a robot before sending.</small>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!capVal || loading}
                    className="brw-join__submit"
                  >
                    {loading ? "Sending..." : "Send Application"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {showSuccessModal && (
        <div
          className="brw-join-modal"
          onClick={() => setShowSuccessModal(false)}
        >
          <div
            className="brw-join-modal__card"
            role="dialog"
            aria-modal="true"
            aria-label="Application sent confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa fa-check-circle brw-join-modal__icon"></i>
            <h4>Application Sent Successfully</h4>
            <p>
              Thank you for applying. Our team will review your information.
            </p>
            <button
              type="button"
              className="brw-join__submit brw-join-modal__button"
              onClick={() => setShowSuccessModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .brw-join {
          padding: 58px 0 94px;
          background:
            linear-gradient(135deg, rgba(8, 28, 56, 0.06) 0%, rgba(255, 168, 0, 0.06) 48%, rgba(8, 28, 56, 0.04) 100%),
            #f4f7fb;
          color: #082347;
        }

        .brw-join__hero {
          display: grid;
          grid-template-columns: minmax(0, 0.93fr) minmax(360px, 1.07fr);
          gap: 28px;
          align-items: stretch;
          margin-bottom: 24px;
        }

        .brw-join__hero-copy,
        .brw-join__media-panel,
        .brw-join__roles-panel,
        .brw-join__application-aside,
        .brw-join__form-card {
          border-radius: 8px;
          border: 1px solid rgba(8, 35, 71, 0.1);
          box-shadow: 0 24px 70px rgba(8, 35, 71, 0.12);
        }

        .brw-join__hero-copy {
          position: relative;
          overflow: hidden;
          min-height: 500px;
          padding: 46px;
          background:
            linear-gradient(145deg, rgba(8, 35, 71, 0.96) 0%, rgba(7, 54, 99, 0.95) 100%),
            #082347;
          color: #fff;
        }

        .brw-join__hero-copy::before,
        .brw-join__hero-copy::after {
          content: "";
          position: absolute;
          left: 38px;
          right: 38px;
          height: 1px;
          background: rgba(255, 255, 255, 0.14);
        }

        .brw-join__hero-copy::before {
          top: 28px;
        }

        .brw-join__hero-copy::after {
          bottom: 28px;
        }

        .brw-join__eyebrow {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          max-width: 100%;
          margin-bottom: 18px;
          padding: 7px 10px;
          border-radius: 999px;
          background: rgba(255, 168, 0, 0.14);
          color: #ffb11f;
          font-size: 0.76rem;
          font-weight: 900;
          letter-spacing: 0.12em;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .brw-join__hero-copy h1 {
          max-width: 640px;
          margin: 0 0 18px;
          color: #fff;
          font-size: clamp(2.7rem, 5vw, 5.65rem);
          line-height: 0.94;
          letter-spacing: 0;
          text-transform: uppercase;
        }

        .brw-join__hero-copy p {
          max-width: 600px;
          margin: 0;
          color: rgba(255, 255, 255, 0.78);
          font-size: 1.05rem;
          line-height: 1.8;
        }

        .brw-join__hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin: 28px 0 34px;
        }

        .brw-join__primary-link,
        .brw-join__secondary-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 900;
          line-height: 1.2;
          text-decoration: none;
          text-transform: uppercase;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .brw-join__primary-link {
          background: #ffad00;
          color: #082347;
          box-shadow: 0 14px 32px rgba(255, 173, 0, 0.28);
        }

        .brw-join__secondary-link {
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .brw-join__primary-link:hover,
        .brw-join__secondary-link:hover {
          transform: translateY(-2px);
          text-decoration: none;
        }

        .brw-join__primary-link:hover {
          color: #082347;
          box-shadow: 0 18px 38px rgba(255, 173, 0, 0.34);
        }

        .brw-join__secondary-link:hover {
          background: rgba(255, 255, 255, 0.14);
          color: #fff;
        }

        .brw-join__stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          margin-top: auto;
        }

        .brw-join__stat {
          min-height: 100px;
          padding: 18px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.08);
        }

        .brw-join__stat strong,
        .brw-join__stat span {
          display: block;
        }

        .brw-join__stat strong {
          margin-bottom: 8px;
          color: #ffb11f;
          font-size: 1.35rem;
          line-height: 1.05;
        }

        .brw-join__stat span {
          color: rgba(255, 255, 255, 0.76);
          font-size: 0.82rem;
          font-weight: 800;
          line-height: 1.45;
          text-transform: uppercase;
        }

        .brw-join__media-panel {
          position: relative;
          min-height: 500px;
          overflow: hidden;
          background: #082347;
        }

        .brw-join__media-panel img {
          width: 100%;
          height: 100%;
          min-height: 500px;
          object-fit: cover;
          display: block;
          filter: saturate(1.08) contrast(1.03);
        }

        .brw-join__media-panel::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(8, 35, 71, 0.02) 40%, rgba(8, 35, 71, 0.78) 100%);
          pointer-events: none;
        }

        .brw-join__media-caption {
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 24px;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 16px 18px;
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 8px;
          background: rgba(8, 35, 71, 0.78);
          backdrop-filter: blur(12px);
        }

        .brw-join__media-caption span,
        .brw-join__media-caption strong {
          color: #fff;
          line-height: 1.2;
        }

        .brw-join__media-caption span {
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .brw-join__media-caption strong {
          font-size: 1.1rem;
        }

        .brw-join__roles-panel {
          display: grid;
          grid-template-columns: minmax(260px, 0.42fr) minmax(0, 0.58fr);
          gap: 24px;
          align-items: center;
          margin-bottom: 24px;
          padding: 28px;
          background: #fff;
        }

        .brw-join__roles-copy span,
        .brw-join__form-head span {
          display: block;
          margin-bottom: 8px;
          color: #ff9f00;
          font-size: 0.77rem;
          font-weight: 900;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .brw-join__roles-copy h2,
        .brw-join__application-aside h2,
        .brw-join__form-head h2 {
          margin: 0;
          color: #082347;
          font-weight: 900;
          letter-spacing: 0;
        }

        .brw-join__roles-copy h2 {
          max-width: 430px;
          font-size: clamp(1.45rem, 2.4vw, 2.2rem);
          line-height: 1.08;
        }

        .brw-join__role-list {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 10px;
        }

        .brw-join__role-pill {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          padding: 0 13px;
          border: 1px solid rgba(8, 35, 71, 0.1);
          border-radius: 999px;
          background: #f4f7fb;
          color: #082347;
          font-size: 0.83rem;
          font-weight: 900;
          line-height: 1.2;
          white-space: nowrap;
        }

        .brw-join__application-grid {
          display: grid;
          grid-template-columns: minmax(310px, 0.43fr) minmax(0, 0.57fr);
          gap: 24px;
          align-items: start;
        }

        .brw-join__application-aside {
          position: sticky;
          top: 24px;
          padding: 34px;
          background: #082347;
          color: #fff;
        }

        .brw-join__application-aside h2 {
          color: #fff;
          font-size: clamp(1.7rem, 3vw, 2.7rem);
          line-height: 1.05;
        }

        .brw-join__application-aside > p {
          margin: 16px 0 0;
          color: rgba(255, 255, 255, 0.74);
          line-height: 1.75;
        }

        .brw-join__process-list {
          display: grid;
          gap: 14px;
          margin: 30px 0;
        }

        .brw-join__process-item {
          display: grid;
          grid-template-columns: 48px 1fr;
          gap: 14px;
          padding: 16px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.08);
        }

        .brw-join__process-item > span {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: #ffad00;
          color: #082347;
          font-weight: 900;
        }

        .brw-join__process-item h3,
        .brw-join__process-item p {
          margin: 0;
        }

        .brw-join__process-item h3 {
          color: #fff;
          font-size: 1.03rem;
          font-weight: 900;
        }

        .brw-join__process-item p {
          margin-top: 5px;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.92rem;
          line-height: 1.55;
        }

        .brw-join__contact-panel {
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 14px;
          align-items: center;
          padding: 16px;
          border-radius: 8px;
          background: #fff;
        }

        .brw-join__contact-panel i {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 8px;
          background: rgba(255, 173, 0, 0.15);
          color: #ff9f00;
          font-size: 1rem;
        }

        .brw-join__contact-panel span,
        .brw-join__contact-panel a {
          display: block;
        }

        .brw-join__contact-panel span {
          color: #65758a;
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .brw-join__contact-panel a {
          margin-top: 2px;
          color: #082347;
          font-weight: 700;
          overflow-wrap: anywhere;
        }

        .brw-join__form-card {
          padding: 34px;
          background: #fff;
        }

        .brw-join__form-head {
          margin-bottom: 24px;
          padding-bottom: 22px;
          border-bottom: 1px solid rgba(8, 35, 71, 0.1);
        }

        .brw-join__form-head h2 {
          font-size: clamp(1.7rem, 3vw, 2.6rem);
          line-height: 1.08;
        }

        .brw-join__form-head p {
          max-width: 580px;
          margin: 10px 0 0;
          color: #65758a;
          line-height: 1.7;
        }

        .brw-join__form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .brw-join__field {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
        }

        .brw-join__field--full {
          grid-column: 1 / -1;
        }

        .brw-join__field span {
          color: #082347;
          font-size: 0.85rem;
          font-weight: 900;
        }

        .brw-join__field input,
        .brw-join__field textarea {
          width: 100%;
          min-height: 54px;
          padding: 14px 15px;
          border: 1px solid rgba(8, 35, 71, 0.16);
          border-radius: 8px;
          background: #f8fafc;
          color: #082347;
          font-size: 0.98rem;
          font-weight: 700;
          box-shadow: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .brw-join__field textarea {
          min-height: 160px;
          resize: vertical;
        }

        .brw-join__field input::placeholder,
        .brw-join__field textarea::placeholder {
          color: #8a98aa;
          font-weight: 600;
        }

        .brw-join__field input:focus,
        .brw-join__field textarea:focus {
          outline: none;
          border-color: #ffad00;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(255, 173, 0, 0.16);
        }

        .brw-join__form-footer {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 16px;
          align-items: end;
          padding-top: 6px;
        }

        .brw-join__captcha {
          max-width: 100%;
          overflow-x: auto;
          padding-bottom: 2px;
        }

        .brw-join__captcha small {
          display: block;
          margin-top: 8px;
          color: #b3261e;
          font-size: 0.82rem;
          font-weight: 800;
        }

        .brw-join__submit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 210px;
          min-height: 54px;
          padding: 0 22px;
          border: 0;
          border-radius: 8px;
          background: #ffad00;
          color: #082347;
          font-size: 0.9rem;
          font-weight: 900;
          line-height: 1.2;
          text-transform: uppercase;
          box-shadow: 0 16px 34px rgba(255, 173, 0, 0.28);
          transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
        }

        .brw-join__submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 40px rgba(255, 173, 0, 0.34);
        }

        .brw-join__submit:disabled {
          opacity: 0.58;
          cursor: not-allowed;
          box-shadow: none;
        }

        .brw-join-modal {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(8, 17, 29, 0.68);
          z-index: 2000;
        }

        .brw-join-modal__card {
          width: 100%;
          max-width: 460px;
          padding: 34px;
          border: 1px solid rgba(8, 35, 71, 0.12);
          border-radius: 8px;
          text-align: center;
          background: #fff;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.24);
        }

        .brw-join-modal__icon {
          margin-bottom: 16px;
          font-size: 3rem;
          color: #37a56a;
        }

        .brw-join-modal__card h4 {
          margin-bottom: 12px;
          color: #082347;
          font-size: 1.7rem;
          font-weight: 900;
        }

        .brw-join-modal__card p {
          margin-bottom: 18px;
          color: #65758a;
          line-height: 1.7;
        }

        .brw-join-modal__button {
          min-width: 160px;
        }

        @media (max-width: 1199px) {
          .brw-join__hero,
          .brw-join__application-grid {
            grid-template-columns: 1fr;
          }

          .brw-join__application-aside {
            position: static;
          }
        }

        @media (max-width: 991px) {
          .brw-join {
            padding: 42px 0 76px;
          }

          .brw-join__hero-copy,
          .brw-join__form-card,
          .brw-join__application-aside {
            padding: 28px;
          }

          .brw-join__hero-copy,
          .brw-join__media-panel,
          .brw-join__media-panel img {
            min-height: auto;
          }

          .brw-join__media-panel img {
            aspect-ratio: 16 / 10;
          }

          .brw-join__roles-panel {
            grid-template-columns: 1fr;
          }

          .brw-join__role-list {
            justify-content: flex-start;
          }

          .brw-join__form-grid,
          .brw-join__form-footer {
            grid-template-columns: 1fr;
          }

          .brw-join__submit {
            width: 100%;
          }
        }

        @media (max-width: 767px) {
          .brw-join__hero-copy h1 {
            font-size: 2.45rem;
          }

          .brw-join__hero-copy {
            padding: 24px;
          }

          .brw-join__hero-actions,
          .brw-join__stats,
          .brw-join__media-caption {
            display: grid;
            grid-template-columns: 1fr;
          }

          .brw-join__media-caption {
            left: 14px;
            right: 14px;
            bottom: 14px;
          }

          .brw-join__roles-panel,
          .brw-join__application-aside,
          .brw-join__form-card {
            padding: 22px;
          }

          .brw-join__role-pill {
            white-space: normal;
          }

          .brw-join__process-item,
          .brw-join__contact-panel {
            grid-template-columns: 1fr;
          }

          .brw-join__process-item > span {
            width: 42px;
            height: 42px;
          }

          .brw-join-modal__card {
            padding: 28px 22px;
          }
        }

        .brw-join {
          padding: 0 0 96px;
          background:
            linear-gradient(180deg, #071d3a 0%, #071d3a 360px, #eef3f8 360px, #eef3f8 100%);
          color: #061f3f;
        }

        .brw-join .container {
          max-width: 1220px;
        }

        .brw-join,
        .brw-join *,
        .brw-join *::before,
        .brw-join *::after {
          box-sizing: border-box;
        }

        .brw-join__hero {
          position: relative;
          display: block;
          min-height: 620px;
          margin: 0 0 26px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.14);
          border-radius: 8px;
          background: #061f3f;
          box-shadow: 0 34px 95px rgba(4, 20, 39, 0.34);
          isolation: isolate;
        }

        .brw-join__hero::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          background:
            linear-gradient(90deg, rgba(5, 21, 43, 0.96) 0%, rgba(5, 21, 43, 0.86) 42%, rgba(5, 21, 43, 0.34) 70%, rgba(5, 21, 43, 0.14) 100%),
            linear-gradient(180deg, rgba(5, 21, 43, 0.08) 0%, rgba(5, 21, 43, 0.88) 100%);
          pointer-events: none;
        }

        .brw-join__hero-copy,
        .brw-join__media-panel,
        .brw-join__roles-panel,
        .brw-join__application-aside,
        .brw-join__form-card {
          box-shadow: none;
        }

        .brw-join__media-panel {
          position: absolute;
          inset: 0;
          z-index: 0;
          min-height: 100%;
          border: 0;
          border-radius: 0;
          background: #061f3f;
        }

        .brw-join__media-panel img {
          width: 100%;
          height: 100%;
          min-height: 620px;
          object-fit: cover;
          object-position: center;
          filter: saturate(1.12) contrast(1.08);
        }

        .brw-join__media-panel::after {
          display: none;
        }

        .brw-join__hero-copy {
          position: relative;
          z-index: 2;
          display: flex;
          min-height: 620px;
          width: min(720px, 62%);
          flex-direction: column;
          justify-content: center;
          padding: 64px;
          border: 0;
          border-radius: 0;
          background: transparent;
          color: #fff;
        }

        .brw-join__hero-copy::before,
        .brw-join__hero-copy::after {
          display: none;
        }

        .brw-join__eyebrow {
          margin-bottom: 16px;
          padding: 8px 11px;
          border: 1px solid rgba(255, 177, 31, 0.26);
          background: rgba(255, 177, 31, 0.13);
          color: #ffb11f;
          font-size: 0.72rem;
          letter-spacing: 0.14em;
        }

        .brw-join__hero-copy h1 {
          max-width: 720px;
          margin: 0 0 18px;
          color: #fff;
          font-size: clamp(3.5rem, 6.3vw, 7.1rem);
          line-height: 0.9;
          text-transform: uppercase;
        }

        .brw-join__hero-copy p {
          max-width: 610px;
          color: rgba(255, 255, 255, 0.82);
          font-size: 1.08rem;
          line-height: 1.78;
        }

        .brw-join__hero-actions {
          gap: 10px;
          margin: 30px 0 38px;
        }

        .brw-join__primary-link,
        .brw-join__secondary-link {
          min-height: 50px;
          padding: 0 22px;
          border-radius: 6px;
          font-size: 0.82rem;
          letter-spacing: 0.04em;
        }

        .brw-join__secondary-link {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(14px);
        }

        .brw-join__stats {
          position: absolute;
          left: 64px;
          right: 64px;
          bottom: 34px;
          z-index: 3;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1px;
          max-width: 780px;
          margin: 0;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.14);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.14);
          backdrop-filter: blur(18px);
        }

        .brw-join__stat {
          min-height: 92px;
          padding: 18px 20px;
          border: 0;
          border-radius: 0;
          background: rgba(6, 31, 63, 0.7);
        }

        .brw-join__stat strong {
          color: #ffb11f;
          font-size: clamp(1.25rem, 2vw, 1.75rem);
        }

        .brw-join__stat span {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.76rem;
          letter-spacing: 0.05em;
        }

        .brw-join__media-caption {
          left: auto;
          right: 30px;
          bottom: 30px;
          z-index: 4;
          display: block;
          width: min(330px, 32vw);
          padding: 18px;
          border-radius: 8px;
          background: #fff;
          border: 1px solid rgba(255, 255, 255, 0.42);
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.26);
          opacity: 0.96;
        }

        .brw-join__media-caption span {
          display: block;
          margin-bottom: 6px;
          color: #ff9f00;
        }

        .brw-join__media-caption strong {
          display: block;
          color: #061f3f;
          font-size: 1.28rem;
          line-height: 1.08;
        }

        .brw-join__roles-panel {
          display: grid;
          grid-template-columns: minmax(270px, 0.35fr) minmax(0, 0.65fr);
          gap: 28px;
          align-items: start;
          margin-bottom: 26px;
          padding: 28px;
          border: 1px solid rgba(6, 31, 63, 0.1);
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 26px 70px rgba(6, 31, 63, 0.1);
        }

        .brw-join__roles-copy {
          display: flex;
          min-height: 0;
          flex-direction: column;
          justify-content: flex-start;
          gap: 14px;
          padding: 24px;
          border-radius: 8px;
          background: #061f3f;
        }

        .brw-join__roles-copy span,
        .brw-join__form-head span {
          color: #ff9f00;
        }

        .brw-join__roles-copy h2 {
          max-width: 420px;
          color: #fff;
          font-size: clamp(1.65rem, 2.8vw, 2.85rem);
          line-height: 1;
          text-transform: uppercase;
        }

        .brw-join__role-list {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          justify-content: stretch;
        }

        .brw-join__role-pill {
          display: grid;
          grid-template-columns: 42px 1fr;
          gap: 12px;
          align-items: center;
          min-height: 74px;
          padding: 12px;
          border: 1px solid rgba(6, 31, 63, 0.1);
          border-radius: 8px;
          background:
            linear-gradient(135deg, rgba(255, 173, 0, 0.1) 0%, rgba(255, 255, 255, 0) 55%),
            #f7f9fc;
          color: #061f3f;
          font-size: 0.94rem;
          white-space: normal;
          transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .brw-join__role-pill:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 173, 0, 0.55);
          box-shadow: 0 16px 34px rgba(6, 31, 63, 0.11);
        }

        .brw-join__role-pill em {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border-radius: 7px;
          background: #ffad00;
          color: #061f3f;
          font-style: normal;
          font-weight: 900;
        }

        .brw-join__application-grid {
          display: grid;
          grid-template-columns: minmax(300px, 0.37fr) minmax(0, 0.63fr);
          gap: 0;
          align-items: stretch;
          overflow: hidden;
          border: 1px solid rgba(6, 31, 63, 0.12);
          border-radius: 8px;
          background: #061f3f;
          box-shadow: 0 30px 80px rgba(6, 31, 63, 0.16);
        }

        .brw-join__application-aside {
          position: relative;
          top: auto;
          padding: 34px;
          border: 0;
          border-radius: 0;
          background:
            linear-gradient(180deg, rgba(255, 173, 0, 0.1) 0%, rgba(255, 173, 0, 0) 36%),
            #061f3f;
        }

        .brw-join__application-aside h2 {
          color: #fff;
          font-size: clamp(1.65rem, 2.8vw, 2.7rem);
          line-height: 1;
          text-transform: uppercase;
        }

        .brw-join__process-list {
          gap: 10px;
          margin: 28px 0;
        }

        .brw-join__process-item {
          grid-template-columns: 52px 1fr;
          padding: 14px;
          border-color: rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.08);
        }

        .brw-join__process-item > span {
          border-radius: 7px;
        }

        .brw-join__contact-panel {
          border-radius: 8px;
        }

        .brw-join__form-card {
          padding: 38px;
          border: 0;
          border-radius: 0;
          background:
            linear-gradient(135deg, rgba(255, 173, 0, 0.08) 0%, rgba(255, 255, 255, 0) 38%),
            #fff;
        }

        .brw-join__form-head {
          margin-bottom: 24px;
          padding-bottom: 22px;
          border-bottom: 1px solid rgba(6, 31, 63, 0.1);
        }

        .brw-join__form-head span {
          margin: 0;
        }

        .brw-join__form-head h2 {
          color: #061f3f;
          font-size: clamp(2.1rem, 4vw, 4rem);
          line-height: 0.95;
          text-transform: uppercase;
        }

        .brw-join__form-head p,
        .brw-join__form-note {
          margin: 0;
          color: #5c6b7f;
          font-size: 0.95rem;
          line-height: 1.65;
        }

        .brw-join__form-note {
          grid-column: 1 / -1;
          max-width: 560px;
          padding: 14px 16px;
          border: 1px solid rgba(6, 31, 63, 0.08);
          border-radius: 8px;
          background: #f7f9fc;
        }

        .brw-join__form-grid {
          gap: 14px;
        }

        .brw-join__field {
          gap: 7px;
        }

        .brw-join__field span {
          color: #061f3f;
          font-size: 0.8rem;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        .brw-join__field input,
        .brw-join__field textarea {
          min-height: 56px;
          border: 1px solid rgba(6, 31, 63, 0.14);
          border-radius: 6px;
          background: #f7f9fc;
        }

        .brw-join__field textarea {
          min-height: 148px;
        }

        .brw-join__form-footer {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(190px, auto);
          gap: 16px;
          align-items: end;
          margin-top: 4px;
          padding: 18px;
          border: 1px solid rgba(6, 31, 63, 0.08);
          border-radius: 8px;
          background: #f7f9fc;
        }

        .brw-join__submit {
          min-height: 56px;
          border-radius: 6px;
          background: #ffad00;
          letter-spacing: 0.04em;
        }

        .brw-join-modal {
          background: rgba(6, 18, 34, 0.76);
          backdrop-filter: blur(8px);
        }

        .brw-join-modal__card {
          border-radius: 8px;
        }

        @media (max-width: 1199px) {
          .brw-join__hero-copy {
            width: 70%;
          }

          .brw-join__application-grid {
            grid-template-columns: 1fr;
          }

          .brw-join__application-aside {
            display: grid;
            grid-template-columns: minmax(0, 0.55fr) minmax(300px, 0.45fr);
            gap: 22px;
            align-items: start;
          }

          .brw-join__application-aside .brw-join__eyebrow,
          .brw-join__application-aside > h2,
          .brw-join__application-aside > p {
            grid-column: 1;
          }

          .brw-join__process-list,
          .brw-join__contact-panel {
            grid-column: 2;
          }

          .brw-join__process-list {
            grid-row: 1 / span 3;
            margin: 0;
          }
        }

        @media (max-width: 991px) {
          .brw-join {
            padding-bottom: 76px;
            background:
              linear-gradient(180deg, #071d3a 0%, #071d3a 240px, #eef3f8 240px, #eef3f8 100%);
          }

          .brw-join__hero,
          .brw-join__hero-copy,
          .brw-join__media-panel img {
            min-height: 600px;
          }

          .brw-join__hero-copy {
            width: 100%;
            padding: 38px;
          }

          .brw-join__media-caption {
            display: none;
          }

          .brw-join__stats {
            left: 38px;
            right: 38px;
            bottom: 28px;
          }

          .brw-join__roles-panel {
            grid-template-columns: 1fr;
          }

          .brw-join__roles-copy {
            min-height: auto;
          }

          .brw-join__application-aside {
            display: block;
          }

          .brw-join__process-list {
            margin: 24px 0;
          }

          .brw-join__form-head {
            grid-template-columns: 1fr;
          }

          .brw-join__form-footer {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 767px) {
          .brw-join__hero,
          .brw-join__hero-copy,
          .brw-join__media-panel img {
            min-height: 680px;
          }

          .brw-join__hero-copy {
            padding: 26px;
            justify-content: flex-start;
          }

          .brw-join__hero-copy h1 {
            font-size: 3.2rem;
          }

          .brw-join__stats,
          .brw-join__role-list,
          .brw-join__form-grid {
            grid-template-columns: 1fr;
          }

          .brw-join__stats {
            left: 18px;
            right: 18px;
            bottom: 18px;
          }

          .brw-join__stat {
            min-height: auto;
          }

          .brw-join__roles-panel,
          .brw-join__application-aside,
          .brw-join__form-card {
            padding: 20px;
          }

          .brw-join__form-head h2 {
            font-size: 2.55rem;
          }

          .brw-join__process-item,
          .brw-join__contact-panel {
            grid-template-columns: 1fr;
          }
        }

        .brw-join {
          padding: 64px 0 96px;
          background: linear-gradient(180deg, #eef3f8 0%, #f7f9fc 100%);
        }

        .brw-join__hero {
          width: 100%;
          min-height: 540px;
          margin: 0 auto 30px;
          border-color: rgba(6, 31, 63, 0.16);
          box-shadow: 0 26px 72px rgba(6, 31, 63, 0.18);
        }

        .brw-join__hero::before {
          background:
            linear-gradient(90deg, rgba(5, 21, 43, 0.94) 0%, rgba(5, 21, 43, 0.82) 45%, rgba(5, 21, 43, 0.35) 76%, rgba(5, 21, 43, 0.18) 100%),
            linear-gradient(180deg, rgba(5, 21, 43, 0.1) 0%, rgba(5, 21, 43, 0.78) 100%);
        }

        .brw-join__media-panel img,
        .brw-join__hero-copy {
          min-height: 540px;
        }

        .brw-join__hero-copy {
          width: min(590px, 58%);
          justify-content: center;
          padding: 46px;
        }

        .brw-join__hero-copy h1 {
          max-width: 560px;
          font-size: clamp(3.2rem, 5vw, 5.6rem);
          line-height: 0.94;
        }

        .brw-join__hero-copy p {
          max-width: 540px;
          font-size: 1rem;
        }

        .brw-join__stats {
          position: relative;
          left: auto;
          right: auto;
          bottom: auto;
          width: min(560px, calc(58% - 72px));
          min-width: min(100%, 520px);
          max-width: none;
          margin: 24px 0 0;
        }

        .brw-join__stat {
          min-height: 82px;
          padding: 15px 16px;
        }

        .brw-join__media-caption {
          display: none;
        }

        @media (max-width: 991px) {
          .brw-join {
            padding-top: 46px;
          }

          .brw-join__hero,
          .brw-join__media-panel img,
          .brw-join__hero-copy {
            min-height: 580px;
          }

          .brw-join__hero-copy {
            width: 100%;
            padding: 34px;
          }

          .brw-join__stats {
            left: auto;
            right: auto;
            width: auto;
          }
        }

        @media (max-width: 767px) {
          .brw-join__hero,
          .brw-join__media-panel img,
          .brw-join__hero-copy {
            min-height: 620px;
          }

          .brw-join__hero-copy {
            padding: 24px;
          }

          .brw-join__hero-copy h1 {
            font-size: 3rem;
          }

          .brw-join__stats {
            left: auto;
            right: auto;
            grid-template-columns: 1fr;
          }
        }

        .brw-join__hero {
          min-height: 500px;
        }

        .brw-join__media-panel img,
        .brw-join__hero-copy {
          min-height: 500px;
        }

        .brw-join__hero-copy {
          width: min(570px, 56%);
          padding: 44px;
        }

        .brw-join__hero-copy h1 {
          max-width: 500px;
          font-size: clamp(2.35rem, 3.35vw, 3.75rem);
          font-weight: 700;
          line-height: 1.08;
          text-transform: none;
        }

        .brw-join__hero-copy p {
          max-width: 520px;
          font-size: 0.98rem;
          line-height: 1.7;
        }

        .brw-join__eyebrow,
        .brw-join__roles-copy span,
        .brw-join__form-head span {
          font-size: 0.7rem;
          letter-spacing: 0.1em;
        }

        .brw-join__primary-link,
        .brw-join__secondary-link,
        .brw-join__submit {
          font-weight: 700;
          letter-spacing: 0.02em;
        }

        .brw-join__stats {
          width: min(520px, calc(56% - 64px));
        }

        .brw-join__stat {
          min-height: 74px;
          padding: 14px 15px;
        }

        .brw-join__stat strong {
          font-size: clamp(1rem, 1.35vw, 1.22rem);
          font-weight: 700;
        }

        .brw-join__stat span {
          font-size: 0.7rem;
          font-weight: 700;
        }

        .brw-join__roles-copy h2,
        .brw-join__application-aside h2,
        .brw-join__form-head h2 {
          font-weight: 700;
          letter-spacing: 0;
          text-transform: none;
        }

        .brw-join__roles-copy h2 {
          font-size: clamp(1.28rem, 1.7vw, 1.75rem);
          line-height: 1.2;
          margin: 0;
        }

        .brw-join__role-pill {
          min-height: 64px;
          font-size: 0.9rem;
          font-weight: 700;
        }

        .brw-join__role-pill em {
          font-weight: 700;
        }

        .brw-join__application-aside h2 {
          font-size: clamp(1.35rem, 1.9vw, 1.95rem);
          line-height: 1.16;
        }

        .brw-join__application-aside > p,
        .brw-join__process-item p,
        .brw-join__form-head p {
          font-size: 0.94rem;
          line-height: 1.65;
        }

        .brw-join__process-item h3 {
          font-size: 1rem;
          font-weight: 700;
        }

        .brw-join__form-head h2 {
          font-size: clamp(1.75rem, 2.55vw, 2.65rem);
          line-height: 1.08;
        }

        .brw-join__field span {
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.01em;
        }

        @media (max-width: 991px) {
          .brw-join__hero,
          .brw-join__media-panel img,
          .brw-join__hero-copy {
            min-height: 540px;
          }

          .brw-join__hero-copy {
            width: 100%;
          }

          .brw-join__stats {
            width: auto;
          }
        }

        @media (max-width: 767px) {
          .brw-join__hero,
          .brw-join__media-panel img,
          .brw-join__hero-copy {
            min-height: 580px;
          }

          .brw-join__hero-copy h1 {
            font-size: 2.15rem;
          }

          .brw-join__form-head h2 {
            font-size: 1.85rem;
          }
        }

        .brw-join__hero,
        .brw-join__media-panel,
        .brw-join__media-panel img {
          border: 0;
          outline: 0;
        }

        .brw-join__application-grid,
        .brw-join__form-card {
          border: 0;
          outline: 0;
        }

        .brw-join__roles-panel {
          grid-template-columns: minmax(240px, 0.31fr) minmax(0, 0.69fr);
          gap: 20px;
          align-items: center;
          padding: 18px;
          border: 0;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 18px 52px rgba(6, 31, 63, 0.08);
        }

        .brw-join__roles-copy {
          gap: 8px;
          padding: 4px 22px 4px 0;
          border-right: 1px solid rgba(6, 31, 63, 0.1);
          border-radius: 0;
          background: transparent;
        }

        .brw-join__roles-copy span {
          margin: 0;
          font-size: 0.68rem;
          font-weight: 800;
        }

        .brw-join__roles-copy h2 {
          max-width: 320px;
          color: #061f3f;
          font-size: clamp(1.2rem, 1.55vw, 1.6rem);
          font-weight: 700;
          line-height: 1.16;
          text-transform: none;
        }

        .brw-join__role-list {
          gap: 8px;
        }

        .brw-join__role-pill {
          grid-template-columns: 32px 1fr;
          gap: 10px;
          min-height: 48px;
          padding: 8px 10px;
          border-color: rgba(6, 31, 63, 0.08);
          background: #fff;
          font-size: 0.86rem;
          font-weight: 700;
          box-shadow: 0 8px 22px rgba(6, 31, 63, 0.04);
        }

        .brw-join__role-pill:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 28px rgba(6, 31, 63, 0.08);
        }

        .brw-join__role-pill em {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: rgba(255, 173, 0, 0.16);
          color: #9d6500;
          font-size: 0.78rem;
          font-weight: 800;
        }

        @media (min-width: 1200px) {
          .brw-join__role-list {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 991px) {
          .brw-join__roles-panel {
            grid-template-columns: 1fr;
          }

          .brw-join__roles-copy {
            padding: 0 0 16px;
            border-right: 0;
            border-bottom: 1px solid rgba(6, 31, 63, 0.1);
          }

          .brw-join__roles-copy h2 {
            max-width: none;
          }
        }
      `}</style>
    </>
  );
}

export default Join;
